import {
  buildMainFieldFilterParams,
  buildMainFieldParamsForRowSelection,
  buildRequestMainFieldParams,
} from '../domain/dataKi/searchParams';
import {
  MAIN_FIELD_TYPE,
  MainFieldType,
  Option,
  QueryParameter,
} from '../domain/schemas';

/**
 * 推移グラフを表示するために必要なパラメータを作成
 *
 * 推移グラフを表示するために、検索時に詳細条件で指定した「機種,SISタイプ,メーカー」は支持率系項目の計算に必要な情報な為、パラメータとしてそれぞれ「kiListForSijiritu,sisTypesForSijiritu,makersForSijiritu」を付与する必要がある。
 * また、選択された行によって「kiList,sisTypes,makers」を付与する必要がある。
 * 仕様：https://www.notion.so/ForSijiritu-1c0a4e41249f81638081c138cb17cd4e
 */
export const dataKiGraphSearchConditionParams = <
  T extends {
    mainField?: MainFieldType;
    kiList?: string[];
    sisTypes?: string[];
    makers?: string[];
    makerGroups?: string[];
    kiListForSijiritu?: string[];
    sisTypesForSijiritu?: string[];
    makersForSijiritu?: string[];
  }
>(
  queryParams: QueryParameter,
  requestParams:
    | {
        hallId: string;
        mainFieldId: string;
        mainFieldRowName?: string;
      }
    | undefined,
  searchParams: T,
  onlyOneKiTagAverageTagIds?: string[],
  makerOptions?: Option[] | undefined
) => {
  // 詳細条件で設定された機種やSISタイプ、メーカーの支持率計算のために必要なパラメータ
  const searchConditionForTypes = {
    kiList: searchParams.kiList,
    makers: searchParams.makers,
    makerGroups: searchParams.makerGroups,
    sisTypes: searchParams.sisTypes,
    kiListForSijiritu: searchParams.kiListForSijiritu,
    sisTypesForSijiritu: searchParams.sisTypesForSijiritu,
    makersForSijiritu: searchParams.makersForSijiritu,
  };

  const isAverageRow = (paramValue: string | null): boolean =>
    paramValue === 'kiTagAverage' ||
    paramValue === 'excludedKiTagAverage' ||
    paramValue ===
      `onlyOneKiTagAverage_${onlyOneKiTagAverageTagIds?.at(0)?.toString()}`;

  const selectedRowMainFieldType = isAverageRow(queryParams.value)
    ? {}
    : buildMainFieldParamsForRowSelection(
        queryParams,
        searchParams,
        makerOptions
      );

  const requestMainFieldParams = buildRequestMainFieldParams(
    requestParams,
    searchParams
  );

  const merged = {
    ...searchConditionForTypes,
    ...selectedRowMainFieldType,
    ...requestMainFieldParams,
  };

  const rowName = requestParams?.mainFieldRowName;

  const narrowByMakerGroup = (groupCode: string) =>
    buildMainFieldFilterParams(
      { name: 'makerGroups', value: groupCode },
      searchParams,
      makerOptions
    );

  const narrowByMaker = (makerCode: string) =>
    buildMainFieldFilterParams(
      { name: 'makers', value: makerCode },
      searchParams,
      makerOptions
    );

  if (
    rowName === 'makerGroups' &&
    searchParams.mainField === MAIN_FIELD_TYPE.MAKER_GROUP
  ) {
    const groupCode = requestMainFieldParams.makerGroups?.at(0);
    if (groupCode !== undefined) {
      return { ...merged, ...narrowByMakerGroup(groupCode) };
    }
  }

  // メーカー行の場合:
  // - 指定メーカーで絞り込む（makers を設定）
  // - 指定されたメーカーが「グループ未所属」の場合は makerGroups を空配列で上書きしてクリアする
  if (rowName === 'makers') {
    const makerCode = requestMainFieldParams.makers?.at(0);
    if (makerCode !== undefined) {
      return { ...merged, ...narrowByMaker(makerCode) };
    }
  }

  if (
    rowName === undefined &&
    searchParams.mainField === MAIN_FIELD_TYPE.MAKER_GROUP &&
    requestParams !== undefined
  ) {
    const groupCode = requestMainFieldParams.makerGroups?.at(0);
    if (groupCode !== undefined) {
      return { ...merged, ...narrowByMakerGroup(groupCode) };
    }
  }

  return merged;
};
